import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { formatPLN, formatPLNCompact } from './chart-utils';

/**
 * The dashboard's stat tile. `emphasis="hero"` is the larger step used by the
 * lead KPI row — the page needs hierarchy, so the hero row reads bigger than
 * the supporting tiles rather than every card being the same weight.
 *
 * Values use proportional figures (no `tabular-nums`): equal-width digits make
 * a large standalone number look loose. `tabular-nums` stays where numbers
 * align vertically — table rows and axis ticks.
 */
export function KpiTile({
  caption,
  value,
  unit = 'PLN',
  emphasis = 'default',
  secondary,
}: {
  caption: string;
  /** Pre-formatted when a string (e.g. a plain headcount); PLN-formatted when a number. */
  value: number | string;
  /** Trailing unit; `null` for a bare count. */
  unit?: string | null;
  emphasis?: 'hero' | 'default';
  secondary?: ReactNode;
}) {
  const text = typeof value === 'number' ? formatPLNCompact(value) : value;
  // The title carries the exact figure (with cents) the compact value drops.
  const exact = typeof value === 'number' ? formatPLN(value) : value;
  const hero = emphasis === 'hero';

  return (
    <Card className={hero ? 'py-4' : 'gap-1 py-3'}>
      <CardContent className={`flex h-full min-w-0 flex-col gap-1 ${hero ? 'px-4' : 'px-3'}`}>
        <p className="text-muted-foreground truncate text-xs font-medium" title={caption}>
          {caption}
        </p>
        <p
          className={`truncate font-semibold tracking-tight ${
            hero ? 'text-lg sm:text-xl xl:text-2xl' : 'text-base sm:text-lg'
          }`}
          title={unit ? `${exact} ${unit}` : exact}
        >
          {text}
          {unit && (
            <span className="text-muted-foreground ml-1 text-sm font-normal">{unit}</span>
          )}
        </p>
        {secondary != null && <div className="mt-auto min-w-0 pt-0.5 text-xs">{secondary}</div>}
      </CardContent>
    </Card>
  );
}
